import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { observerApi } from "../services/observerApi";
import { REVIEW_DIMENSIONS } from "../data/reviewDimensions";

export default function ObserverReviewPage() {
  const [params] = useSearchParams(),
    entity = params.get("entity") || "",
    name = params.get("name") || "this practitioner";
  const [email, setEmail] = useState(""),
    [code, setCode] = useState(""),
    [sent, setSent] = useState(false),
    [verified, setVerified] = useState(
      Boolean(localStorage.getItem("observer_review_session")),
    );
  const [cls, setCls] = useState("verified_purchase"),
    [conflict, setConflict] = useState<boolean>(),
    [body, setBody] = useState(""),
    [displayName, setDisplayName] = useState(""),
    [relationship, setRelationship] = useState("client"),
    [experienceDate, setExperienceDate] = useState(""),
    [ratings, setRatings] = useState<Record<string, number>>({}),
    [evidence, setEvidence] = useState<File>(),
    [adult, setAdult] = useState(false),
    [message, setMessage] = useState("");
  useEffect(() => {
    observerApi.event("review_started", undefined, { class: cls });
    const attribution = localStorage.getItem("observer_share_attribution");
    if (attribution) observerApi.shareEvent(attribution, "review_start").catch(() => undefined);
  }, [cls]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const review = await observerApi.review(entity, {
        class: cls,
        displayName,
        relationshipType: relationship,
        experienceDate: new Date(experienceDate).toISOString(),
        conflictDeclared: conflict,
        reviewerAdultConfirmed: adult,
        body,
        dimensions: cls === "verified_purchase" ? ratings : {},
      });
      observerApi.event("review_submitted", undefined, {
        class: cls,
        conflict_declared: Boolean(conflict),
      });
      const attribution = localStorage.getItem("observer_share_attribution");
      if (attribution) { await observerApi.shareEvent(attribution, "review_submit").catch(() => undefined); localStorage.removeItem("observer_share_attribution"); }
      if (evidence && review.reviewId) {
        await observerApi.evidence(review.reviewId, evidence);
        observerApi.event("evidence_uploaded");
      }
      setMessage(
        (review.crisisSignposting ? review.crisisSignposting + " " : "") +
          "Your review was submitted for human moderation. The subject will be notified after publication and can reply, but will never see your evidence.",
      );
    } catch (x: any) {
      setMessage(x?.response?.data?.error || "Submission failed.");
    }
  };
  if (message)
    return (
      <div className="ot-page">
        <div className="ot-empty">
          <h1>Review status</h1>
          <p>{message}</p>
        </div>
      </div>
    );
  if (!verified)
    return (
      <div className="ot-page">
        <header className="ot-page-head">
          <span className="section-kicker">
            Firsthand experiences and informed opinions
          </span>
          <h1>Verify your email before reviewing.</h1>
          <p>
            Whether you purchased coaching, booked a tarot or psychic reading,
            attended a mediumship session, joined a manifestation or law of
            attraction course, or followed free spiritual teachings, every
            review class requires a verified email. Your public display name may
            still be a pseudonym.
          </p>
        </header>
        <form
          className="ot-form"
          onSubmit={async (e) => {
            e.preventDefault();
            try {
              if (!sent) {
                await observerApi.startEmail(email);
                setSent(true);
              } else {
                await observerApi.verifyEmail(email, code);
                setVerified(true);
              }
            } catch (x: any) {
              setMessage(x?.response?.data?.error || "Verification failed.");
            }
          }}
        >
          <label>
            Email
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          {sent && (
            <label>
              Verification code
              <input
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </label>
          )}
          <button>{sent ? "Verify email" : "Send code"}</button>
        </form>
      </div>
    );
  return (
    <div className="ot-page">
      <header className="ot-page-head">
        <span className="section-kicker">
          Review a spiritual or transformational practice
        </span>
        <h1>Your experience with {name}</h1>
        <p>
          Choose the evidence class that matches your relationship: a paid
          course, coaching relationship, reading, session, or service; an
          unverified client experience; or an opinion about free teachings and
          public content. Reviews never publish automatically.
        </p>
      </header>
      <button
        type="button"
        onClick={() => {
          localStorage.removeItem("observer_review_session");
          localStorage.removeItem("observer_account");
          setVerified(false);
        }}
      >
        Use another verified email
      </button>
      <form className="ot-form" onSubmit={submit}>
        <label>
          Review class
          <select value={cls} onChange={(e) => setCls(e.target.value)}>
            <option value="verified_purchase">Verified purchase</option>
            <option value="unverified_client">Unverified client</option>
            <option value="content_opinion">Content opinion</option>
          </select>
        </label>
        <label>
          Display name or pseudonym
          <input
            required
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </label>
        <label>
          What did you experience?
          <input
            required
            value={relationship}
            onChange={(e) => setRelationship(e.target.value)}
            placeholder="e.g. coaching, tarot reading, course, mediumship session, free videos"
          />
        </label>
        <label>
          Approximate experience date
          <input
            required
            type="date"
            data-testid="experience-date"
            value={experienceDate}
            onInput={(e) =>
              setExperienceDate((e.target as HTMLInputElement).value)
            }
            onChange={(e) => setExperienceDate(e.target.value)}
          />
        </label>
        <fieldset>
          <legend>
            Do you currently offer coaching, readings, or similar services in
            this field?
          </legend>
          <label>
            <input
              type="radio"
              name="conflict"
              required={cls !== "verified_purchase"}
              onChange={() => setConflict(true)}
            />{" "}
            Yes
          </label>
          <label>
            <input
              type="radio"
              name="conflict"
              required={cls !== "verified_purchase"}
              onChange={() => setConflict(false)}
            />{" "}
            No
          </label>
          {cls === "verified_purchase" && (
            <small>Optional for verified-purchase reviews.</small>
          )}
        </fieldset>
        {cls === "verified_purchase" && (
          <>
            {REVIEW_DIMENSIONS.map((dimension) => (
              <label key={dimension.key}>
                {dimension.label}
                {dimension.guidance && <small>{dimension.guidance}</small>}
                <select
                  required
                  value={ratings[dimension.key] || ""}
                  onChange={(e) =>
                    setRatings((current) => ({
                      ...current,
                      [dimension.key]: Number(e.target.value),
                    }))
                  }
                >
                  <option value="">Choose</option>
                  {(dimension.options ||
                    [1, 2, 3, 4, 5].map((value) => ({
                      value,
                      label: String(value),
                    }))
                  ).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            ))}
            <label>
              Private purchase evidence
              <input
                required
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => setEvidence(e.target.files?.[0])}
              />
              <small>
                Stored separately. Never published or shown to the practitioner.
              </small>
            </label>
          </>
        )}
        <label>
          <span>
            <input
              type="checkbox"
              required
              checked={adult}
              onChange={(e) => setAdult(e.target.checked)}
            />{" "}
            I confirm I am 18 or older.
          </span>
        </label>
        <label>
          Your own experience
          <textarea
            required
            minLength={30}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Describe what you personally experienced and what another seeker should understand. Do not assess whether a spiritual belief is true or name unrelated third parties."
          />
        </label>
        <button className="button button-ink">Submit for moderation</button>
      </form>
    </div>
  );
}
